import React from 'react';
import { trackContact } from '../lib/pixel';
import { logger } from '../lib/logger';

/** Official Telegram logo SVG (white plane inside the brand background). */
function TelegramLogo({ className = '', bg = false }) {
  // When `bg=true` we render the colored circle bg (use when the button itself
  // is NOT brand-blue, e.g. ghost/outline). When false, draw only the plane
  // (the parent button supplies the brand background).
  if (bg) {
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#229ED9" />
        <path d="M17.4 7.4 15.6 16.2c-.13.6-.5.74-1 .46l-2.77-2.04-1.34 1.29c-.15.15-.27.27-.55.27l.2-2.83 5.14-4.64c.22-.2-.05-.31-.34-.11l-6.35 4-2.73-.86c-.6-.18-.6-.6.13-.88l10.65-4.11c.49-.18.92.12.75 1.16Z" fill="#fff"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path d="M21.94 4.95c.27-1.21-.4-1.85-1.43-1.45L2.3 10.62c-1.27.51-1.26 1.25-.22 1.56l4.59 1.43 1.78 5.5c.21.65.4.91.95.91.43 0 .61-.2.84-.43l2.04-1.98 4.24 3.13c.78.43 1.34.21 1.54-.72l2.81-13.07ZM7.65 14.27l9.16-5.78c.4-.27.78-.13.47.16l-7.43 6.7-.29 3.16-1.91-4.24Z" fill="currentColor"/>
    </svg>
  );
}

/**
 * Reusable Telegram CTA — opens t.me/HCFGreenParadise_bot in a new tab.
 *
 * Variants:
 *   - "solid"   : Telegram blue filled (use for primary placements)
 *   - "outline" : transparent with brand border (secondary CTA)
 *   - "ghost"   : minimal inline link
 *   - "pill"    : compact rounded badge (for headers / sticky bars)
 */
export const TELEGRAM_BOT_URL = 'https://t.me/HCFGreenParadise_bot';

export default function TelegramButton({
  variant = 'solid',
  label = 'Scrivici su Telegram',
  size = 'md',
  className = '',
  source = 'cta',
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-semibold tracking-[0.04em] transition-all duration-150 whitespace-nowrap';
  const sizes = {
    sm: 'h-9 px-3 text-[12px] rounded-full',
    md: 'h-11 px-5 text-sm rounded-full',
    lg: 'h-12 md:h-14 px-5 md:px-7 text-sm md:text-base rounded-full',
  };
  const variants = {
    solid:
      'bg-[#229ED9] text-white hover:bg-[#1E8FC2] shadow-lg hover:shadow-xl',
    outline:
      'bg-white text-[#229ED9] border border-[#229ED9]/40 hover:bg-[#229ED9]/8 hover:border-[#229ED9]',
    ghost:
      'bg-transparent text-[#229ED9] hover:underline underline-offset-4',
    pill:
      'bg-[#229ED9]/10 text-[#229ED9] hover:bg-[#229ED9]/20 h-8 px-3 text-[11px] rounded-full',
  };

  const finalSize = variant === 'pill' ? '' : sizes[size] || sizes.md;
  const iconSize =
    size === 'lg' ? 'w-5 h-5 md:w-6 md:h-6' : size === 'sm' ? 'w-4 h-4' : 'w-4 h-4 md:w-5 md:h-5';
  // For solid we render only the white plane on brand bg; for others use full logo with bg.
  const logoBg = variant !== 'solid';

  return (
    <a
      href={`${TELEGRAM_BOT_URL}?start=${encodeURIComponent(source)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        try { trackContact({ channel: 'telegram', source }); } catch (err) {
          logger.debug('TelegramButton: pixel trackContact failed', err);
        }
      }}
      className={`${base} ${finalSize} ${variants[variant]} ${className}`}
      data-testid={`telegram-cta-${source}`}
    >
      <TelegramLogo className={iconSize} bg={logoBg} />
      <span>{label}</span>
    </a>
  );
}
